import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  API,
  ca_am_Role_retrieveAll_order,
  ca_am_Role_retrieveAllInUserContext_order,
  ManagedUserClientDTO,
  ManagedUserDTO,
  Order,
  Pagination,
  RoleDTO,
  UserClientFullDTO,
} from '../../../../services/api/api';
import { BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../../../services/util.service';
import { UserService } from '../../../../services/user.service';
import { ErrorService } from '../../../../services/error.service';
import { PermissionService } from '../../../../services/permission.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { Mutable, SbBooleanToggleModel } from '../../../../services/global/global-interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-client-user-edit',
  templateUrl: './client-user-edit-modal.component.html',
  styleUrls: ['./client-user-edit-modal.component.scss'],
  standalone: false,
})
export class ClientUserEditModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  loading: boolean;
  roles: RoleDTO[];
  personalPermissions: string[];
  user: Mutable<ManagedUserClientDTO>;
  userId: number;
  debounceTimer: ReturnType<typeof setTimeout>;
  debounceTimerRole: ReturnType<typeof setTimeout>;
  roleSearch: string;
  permissionSearch: string;
  displayedPermissions: string[];
  transSub;
  welcomeMail: boolean;
  initialBooleans: SbBooleanToggleModel[];
  form: UntypedFormGroup;
  formRoles: UntypedFormGroup;
  formPermissions: UntypedFormGroup;
  rolePagination: BehaviorSubject<Pagination>;
  collectionSizeRole: number;
  maxSizeRole: number;
  resultCountRole: number;
  paginationRoleInitialized: boolean;
  rolesLoading: boolean;
  waitingForServerResponse: boolean;
  displayedRoles: any[];

  constructor(
    private readonly api: API,
    public readonly userStatus: UserService,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    public readonly translate: TranslateService,
    private readonly cdRef: ChangeDetectorRef,
    public readonly permissionService: PermissionService,
    private readonly modalNavigation: ModalNavigationService,
    private readonly modal: ModalService
  ) {
    this.loading = true;
    this.roles = [];
    this.personalPermissions = [];
    this.user = undefined;
    this.userId = undefined;
    this.debounceTimer = null;
    this.debounceTimerRole = null;
    this.roleSearch = '';
    this.permissionSearch = '';
    this.displayedPermissions = [];
    this.displayedRoles = [];
    this.welcomeMail = false;
    this.initialBooleans = [];
    this.form = new UntypedFormGroup({
      Firstname: new UntypedFormControl('', Validators.required),
      Lastname: new UntypedFormControl('', Validators.required),
      Email: new UntypedFormControl('', Validators.required),
    });
    this.formRoles = new UntypedFormGroup({});
    this.formPermissions = new UntypedFormGroup({});
    this.pageRole = 1;
    this.maxSizeRole = 99;
    this.rolePagination = new BehaviorSubject<Pagination>({
      page: this.pageRole ? this.pageRole : 1,
      count: this.maxSizeRole ? this.maxSizeRole : 8,
      pages: -1,
      resultcount: -1,
    });
    this.collectionSizeRole = -1;
    this.resultCountRole = -1;
    this.paginationRoleInitialized = false;
    this.rolesLoading = true;
  }

  // tslint:disable-next-line:variable-name
  pageRole: number;

  get Firstname() {
    return this.form.get('Firstname');
  }

  get Lastname() {
    return this.form.get('Lastname');
  }

  get Email() {
    return this.form.get('Email');
  }

  ngOnInit(): void {
    this.loading = true;
    // wait for modal animation
    setTimeout(() => {
      this.userId = +this.modalNavigation.getCurrentNavigation().data['clientUserID'];
      if (isNaN(this.userId)) {
        this.userId = undefined;
      }
      this.refreshBoolToggles();
      this.initRolePagination();
    }, 300);
  }

  initRolePagination() {
    this.rolePagination.subscribe((e: Pagination) => {
      this.collectionSizeRole = e.resultcount;
      this.maxSizeRole = e.count;
      this.resultCountRole = e.resultcount;
    });

    this.requestDebounced();
  }

  roleSearchChanged($event: string) {
    this.roleSearch = $event;
    this.refreshDisplayedRoles();
  }

  refreshDisplayedRoles() {
    this.displayedRoles = this.filteredRoles();
    this.cdRef.detectChanges();
  }

  filteredRoles(): RoleDTO[] {
    if (this.roles) {
      if (this.roleSearch) {
        const ret: RoleDTO[] = [];
        for (const role of this.roles) {
          const translated: string = this.translate.instant(role.name);
          if (
            role.name === this.roleSearch ||
            role.id.toString().includes(this.roleSearch.toLowerCase()) ||
            translated.toLowerCase() === this.roleSearch.toLowerCase() ||
            role.name.toLowerCase().includes(this.roleSearch.toLowerCase()) ||
            translated.toLowerCase().includes(this.roleSearch.toLowerCase())
          ) {
            ret.push(role);
          }
        }

        return ret;
      }
    }
    return this.roles;
  }

  toggleUserStatus() {
    let userMessage: string;
    let userTitle: string;

    if (this.user && !this.user.blocked) {
      userTitle = this.translate.instant('lang.userEdit.blockUser');
      userMessage = this.translate.instant('lang.userEdit.blockUserQuestion');
    } else {
      userTitle = this.translate.instant('lang.userEdit.unblockUser');
      userMessage = this.translate.instant('lang.userEdit.unblockUserQuestion');
    }
    this.modal
      .confirm({
        title: userTitle,
        message: userMessage,
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          if (this.user && !this.user.blocked) {
            this.api
              .ca_am_User_blockUser(this.user)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: data => {
                  this.user = data;
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          } else {
            this.api
              .ca_am_User_unblockUser(this.user)
              .pipe(takeUntilDestroyed(this.destroyRef), take(1))
              .subscribe({
                next: data => {
                  this.user = data;
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          }
        }
      });
  }

  onPermissionSearch($event: string) {
    this.permissionSearch = $event;
    this.refreshDisplayedPermissions();
  }

  refreshDisplayedPermissions() {
    this.displayedPermissions = this.filteredPermissions();
    this.cdRef.detectChanges();
  }

  filteredPermissions(): string[] {
    if (this.personalPermissions) {
      if (this.permissionSearch) {
        const ret: string[] = [];
        for (const perm of this.personalPermissions) {
          const translated: string = this.translate.instant('permission.' + perm);
          if (
            perm === this.permissionSearch ||
            translated.toLowerCase() === this.permissionSearch.toLowerCase() ||
            perm.toLowerCase().includes(this.permissionSearch.toLowerCase()) ||
            translated.toLowerCase().includes(this.permissionSearch.toLowerCase())
          ) {
            ret.push(perm);
          }
        }

        return ret;
      }
    }
    return this.personalPermissions;
  }

  refreshFromServer() {
    this.loading = true;
    if (!this.userId) {
      this.user = new UserClientFullDTO();
      this.user.user = new ManagedUserDTO();
      this.user.explicitRoles = [];
      this.user.explicitPermissions = [];
      this.getPermissionSettings();
      this.cdRef.detectChanges();
    } else {
      this.api
        .ca_am_User_retrieveById(this.userId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: UserClientFullDTO) => {
            this.user = data;
            this.getPermissionSettings();
            this.cdRef.detectChanges();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  requestRoles(): Promise<void> {
    return new Promise(resolve => {
      this.rolesLoading = true;
      let obs: Observable<RoleDTO[]>;
      if (this.userId) {
        obs = this.api.ca_am_Role_retrieveAllInUserContext(
          this.userId,
          {
            search: this.util.encodeSearchQueryParam(false, this.roleSearch),
            order: { by: ca_am_Role_retrieveAllInUserContext_order.CLIENT_USER_HAS_ROLE, direction: Order.desc },
          },
          this.rolePagination
        );
      } else {
        obs = this.api.ca_am_Role_retrieveAll(
          {
            canAddUser: true,
            search: this.util.encodeSearchQueryParam(false, this.roleSearch),
            order: { by: ca_am_Role_retrieveAll_order.NAME, direction: Order.asc },
          },
          this.rolePagination
        );
      }
      obs
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.rolesLoading = false;
            resolve();
          })
        )
        .subscribe({
          next: (data: RoleDTO[]) => {
            this.paginationRoleInitialized = true;
            if (this.roles) {
              for (const role of this.roles) {
                this.formRoles.removeControl(role.name);
              }
            }
            this.roles = data;
            this.addFormControls(data);
            this.refreshDisplayedRoles();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  private addFormControls(roles: RoleDTO[]): void {
    roles.forEach(r => {
      const userHasRole = this.user.explicitRoles.some(ur => r.name === ur.name);
      this.formRoles.addControl(r.name, new UntypedFormControl(userHasRole));
    });
  }

  getPermissionSettings() {
    this.requestRoles().then(() => {
      this.api
        .shared_Client_retrievePersonalPermissions()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (data: string[]) => {
            this.personalPermissions = data;
            this.personalPermissions.forEach(s => {
              this.formPermissions.addControl(s, new UntypedFormControl(this.user.explicitPermissions.includes(s)));
            });

            this.refreshDisplayedPermissions();

            if (this.transSub) {
              try {
                this.transSub.unsubscribe();
              } catch (err) {}
              this.transSub = null;
            }

            this.transSub = this.translate.onDefaultLangChange.subscribe(() => {
              this.refreshDisplayedPermissions();
            });

            this.cdRef.detectChanges();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    });
  }

  updateUser() {
    this.waitingForServerResponse = true;

    if (!this.userId) {
      this.handleNewUser();
    } else {
      this.handleExistingUser();
    }
  }

  private handleNewUser() {
    if (!this.user.user.id) {
      this.registerAndAddUser();
    } else {
      this.addUser();
    }
  }

  private handleExistingUser() {
    this.api
      .ca_am_User_editUser(this.user)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.waitingForServerResponse = false;
        })
      )
      .subscribe({
        next: (data: UserClientFullDTO) => {
          this.user = data;
          this.close();
        },
        error: error => {
          this.handleError(error);
        },
      });
  }

  private registerAndAddUser() {
    const options = this.welcomeMail ? { mailLanguage: this.userStatus.selectedLanguage() } : null;
    this.api
      .shared_User_registerUser(this.user.user, options)
      .pipe(take(1))
      .subscribe({
        next: (userData: ManagedUserDTO) => {
          this.user.user = userData;
          this.addUser(true);
        },
        error: error => {
          this.handleError(error);
        },
      });
  }

  private addUser(skipMailCheck?: boolean) {
    const options = !skipMailCheck && this.welcomeMail ? { mailLanguage: this.userStatus.selectedLanguage() } : null;

    this.api
      .ca_am_User_addUser(this.user, options)
      .pipe(take(1))
      .subscribe({
        next: user => {
          this.handleUserClientData(user);
        },
        error: error => {
          this.handleError(error);
        },
      });
  }

  private handleUserClientData(userClientData: ManagedUserClientDTO) {
    this.waitingForServerResponse = false;
    this.userId = userClientData.user.id;
    this.user = userClientData;
    this.close();
  }

  handleError(error: any) {
    this.waitingForServerResponse = false;
    this.errorHandler.errorHandler(error);
    this.close(false);
  }

  updateRoles(role: RoleDTO, value: boolean) {
    if (!this.user || !role) {
      return;
    }
    if (value === true) {
      this.user.explicitRoles = this.user.explicitRoles.filter(e => e.id !== role.id);
      this.user.explicitRoles.push(role);
    } else {
      this.user.explicitRoles = this.user.explicitRoles.filter(e => e.id !== role.id);
    }
    this.previewUser();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  updateUserPermissions(permission: string, value: boolean) {
    if (!this.user || !permission) {
      return;
    }
    if (value === true) {
      this.user.explicitPermissions = this.user.explicitPermissions.filter(e => e !== permission);
      this.user.explicitPermissions.push(permission);
    } else {
      this.user.explicitPermissions = this.user.explicitPermissions.filter(e => e !== permission);
    }
    this.previewUser();
  }

  previewUser() {
    if (!this.userId || !this.user.user.id) {
      const userPreview = { ...this.user };
      userPreview.user.firstname = ' ';
      userPreview.user.lastname = ' ';
      userPreview.user.email = ' ';
      this.api
        .ca_am_User_addUserPreview(userPreview)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: UserClientFullDTO) => {
            this.user.effectivePermissions = data.effectivePermissions;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .ca_am_User_editUserPreview(this.user)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (data: UserClientFullDTO) => {
            this.user.effectivePermissions = data.effectivePermissions;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  setUserInfo(attribute: string, value: string) {
    if (!this.user || !value) {
      return;
    }
    if (attribute === 'firstname') {
      this.user.user.firstname = value;
    }
    if (attribute === 'lastname') {
      this.user.user.lastname = value;
    }
    if (attribute === 'email') {
      this.user.user.email = value;
      if (!this.userId) {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.api
            .shared_User_findUserByEmail(value)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (data: ManagedUserDTO) => {
                this.user.user = data;
              },
              error: () => {
                // User does not exist yet
                this.user.user.id = undefined;
              },
            });
        }, 500);
      }
    }
  }

  hasEffectivePermission(val: string) {
    return this.user?.effectivePermissions?.includes(val);
  }

  hasExplicitPermission(val: string) {
    return this.user?.explicitPermissions?.includes(val);
  }

  refreshBoolToggles() {
    this.initialBooleans = [];
    this.initialBooleans = [
      {
        id: 'welcomeMail',
        name: 'Welcome Mail',
        value: this.welcomeMail,
        class: 'welcomeMail',
        classPreview: 'blue',
      },
    ];
  }

  boolChange(t: string, $e: boolean) {
    if (t === 'welcomeMail') {
      this.welcomeMail = $e;
    }
  }

  selectAllPermissions() {
    this.user.explicitPermissions = this.displayedPermissions;
    this.previewUser();
  }

  selectNoPermissions() {
    this.user.explicitPermissions = [];
    this.previewUser();
  }

  close(success: boolean = true) {
    this.modalNavigation.result(success);
  }
}
